import React, { useCallback, useEffect, useState } from "react";
import { Bot, Check, MessageSquarePlus, RotateCcw, Sparkles, Trash2, X } from "lucide-react";
import { offerApi } from "../../../services/offerApi";
import { formatDate } from "../EditorChrome";
import { BLOCK_TYPES } from "./proposalOps";
import { AttachButton, AttachmentThumbs, useImageAttachments } from "./attachments";

const POLL_MS = 15000;

// Commentaires d'une proposition (ou d'une présentation : `api` = presentationApi), relus régulièrement : Nexus ou
// un collègue peuvent les traiter.
export const useProposalComments = (proposalId, onError, api = offerApi) => {
  const [comments, setComments] = useState([]);
  const refresh = useCallback(async () => {
    try {
      setComments((await api.comments(proposalId)).items || []);
    } catch (error) {
      onError?.(error.message);
    }
  }, [proposalId, onError, api]);

  useEffect(() => {
    refresh();
    const timer = setInterval(() => { if (!window.document.hidden) refresh(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  return { comments, refresh };
};

const blockLabel = (slide, blockId) => {
  const block = slide?.blocks?.find((item) => item.id === blockId);
  return block ? (BLOCK_TYPES.find((item) => item.type === block.type)?.label || block.type).toLowerCase() : null;
};

const Target = ({ comment, slides, pages, onGo, part = "slide" }) => {
  const slide = slides.find((item) => item.id === comment.slide_id);
  if (!comment.slide_id) return <span>Tout le document</span>;
  if (!slide) return <span className="italic">{part === "section" ? "Section supprimée" : "Slide supprimée"}</span>;
  const block = comment.block_id ? blockLabel(slide, comment.block_id) : null;
  return (
    <button type="button" onClick={() => onGo(slide.id, comment.block_id)} className="text-left text-blue-700 hover:underline">
      {part === "section" ? "Section" : "Slide"} {pages[slide.id] ?? "masquée"} · {slide.title || "sans titre"}{block ? ` · ${block}` : ""}
    </button>
  );
};

const CommentsPanel = ({
  proposalId, comments, refresh, slides, pages, currentSlide, selectedBlockId, canEdit, userEmployeeId,
  onGo, onAskNexus, onClose, onError, api = offerApi, part = "slide",
}) => {
  const [body, setBody] = useState("");
  const [onBlock, setOnBlock] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const attachments = useImageAttachments();
  const open = comments.filter((comment) => comment.status === "open");
  const shown = showResolved ? comments : open;
  const block = selectedBlockId ? blockLabel(currentSlide, selectedBlockId) : null;

  const run = async (action) => {
    setBusy(true);
    try {
      await action();
      await refresh();
    } catch (error) {
      onError(error.message);
    } finally {
      setBusy(false);
    }
  };

  const add = (event) => {
    event.preventDefault();
    if (!body.trim() || attachments.busy) return;
    run(async () => {
      await api.addComment(proposalId, {
        slide_id: currentSlide?.id, block_id: onBlock && selectedBlockId ? selectedBlockId : undefined, body: body.trim(),
        attachments: attachments.items.map((item) => item.ref),
      });
      setBody("");
      attachments.clear();
    });
  };

  const canManage = (comment) => canEdit || comment.author_employee_id === userEmployeeId;

  return (
    <aside className="w-full shrink-0 space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:w-80">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-slate-900">Commentaires</h2>
        <button type="button" onClick={onClose} aria-label="Fermer les commentaires" className="rounded p-1 text-slate-400 hover:bg-slate-100">
          <X className="h-4 w-4" />
        </button>
      </div>

      <form onSubmit={add} className="space-y-2">
        <label className="block text-xs font-medium text-slate-600" htmlFor="offer-comment">
          {part === "section" ? (currentSlide ? "Sur la section" : "Sur tout le rapport") : "Sur la slide"}{" "}
          {currentSlide ? pages[currentSlide.id] ?? "masquée" : part === "section" ? "" : "?"}
          {currentSlide?.title ? ` · ${currentSlide.title}` : ""}
        </label>
        <textarea id="offer-comment" data-own-undo rows={3} value={body} maxLength={4000}
          onChange={(event) => setBody(event.target.value)} {...attachments.inputHandlers}
          placeholder="Ex. raccourcir les travaux à quatre puces, mettre le prérequis en encart…"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
        {block && (
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={onBlock} onChange={(event) => setOnBlock(event.target.checked)} />
            Viser le bloc sélectionné ({block})
          </label>
        )}
        <AttachmentThumbs items={attachments.items} onRemove={attachments.remove} size={40} />
        {attachments.error && <p className="text-xs text-red-600">{attachments.error}</p>}
        <div className="flex items-center gap-1">
          <button type="submit" disabled={busy || attachments.busy || !body.trim()}
            className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs text-white disabled:opacity-40">
            <MessageSquarePlus className="h-3.5 w-3.5" /> Commenter
          </button>
          <AttachButton attachments={attachments} label="Joindre une image au commentaire" />
        </div>
      </form>

      {canEdit && open.length > 0 && (
        <button type="button" onClick={onAskNexus}
          className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-medium text-blue-800 hover:bg-blue-100">
          <Sparkles className="h-4 w-4" /> Demander à Nexus d'appliquer {open.length > 1 ? `les ${open.length} commentaires` : "le commentaire"}
        </button>
      )}

      <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-xs text-slate-500">
        <span>{open.length} ouvert{open.length > 1 ? "s" : ""}</span>
        <label className="inline-flex items-center gap-1.5">
          <input type="checkbox" checked={showResolved} onChange={(event) => setShowResolved(event.target.checked)} />
          Afficher les traités
        </label>
      </div>

      <ol className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
        {shown.length === 0 && <li className="text-sm text-slate-500">Aucun commentaire {showResolved ? "" : "ouvert"}.</li>}
        {shown.map((comment) => (
          <li key={comment.id} className={`rounded-lg border p-3 text-sm ${
            comment.status === "open" ? "border-amber-200 bg-amber-50/40" : "border-slate-200 opacity-80"}`}>
            <div className="flex items-center gap-1 text-xs text-slate-500">
              {comment.source === "agent" && <Bot className="h-3 w-3" />}
              <span className="font-medium text-slate-700">{comment.author_name || "—"}</span> · {formatDate(comment.created_at)}
            </div>
            <div className="mt-0.5 text-xs"><Target comment={comment} slides={slides} pages={pages} onGo={onGo} part={part} /></div>
            <p className="mt-1 whitespace-pre-wrap text-slate-800">{comment.body}</p>
            {comment.attachments?.length > 0 && (
              <div className="mt-2">
                <AttachmentThumbs items={comment.attachments.map((ref) => ({ ref, name: ref }))} size={56} />
              </div>
            )}
            {comment.reply && (
              <p className="mt-2 rounded bg-emerald-50 px-2 py-1 text-xs text-emerald-800">
                {comment.resolved_by_name ? `${comment.resolved_by_name} : ` : ""}{comment.reply}
              </p>
            )}
            {canManage(comment) && (
              <div className="mt-2 flex gap-1">
                {comment.status === "open" ? (
                  <button type="button" disabled={busy} onClick={() => run(() => api.resolveComment(comment.id, null))}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-600 hover:bg-white">
                    <Check className="h-3 w-3" /> Traité
                  </button>
                ) : (
                  <button type="button" disabled={busy} onClick={() => run(() => api.reopenComment(comment.id))}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-600 hover:bg-white">
                    <RotateCcw className="h-3 w-3" /> Rouvrir
                  </button>
                )}
                <button type="button" disabled={busy} onClick={() => run(() => api.deleteComment(comment.id))}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-slate-500 hover:bg-white hover:text-red-600">
                  <Trash2 className="h-3 w-3" /> Supprimer
                </button>
              </div>
            )}
          </li>
        ))}
      </ol>
    </aside>
  );
};

export default CommentsPanel;
